import { Button, Title } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { generatePath, Link, useMatch } from "react-router";
import { useAnlagenStromstgGenerators } from "../../../hooks/useAnlagenStromstgGenerators";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { ROUTES } from "../../../routes";
import type { AnlageStromstgListData } from "../../../utils/backend-types";
import { DecimalLabel } from "../../BuildingBlocks/DecimalLabel/DecimalLabel";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import "./AnlageStromstgList.scss";

interface AnlageStromstgListProps {
  list: Array<AnlageStromstgListData>;
}

function AnlageStromstgList({ list }: AnlageStromstgListProps) {
  const totalBruttoLeistung = list.reduce(
    (total, generator) => total + (generator.bruttoleistung ?? 0),
    0
  );

  const columns = useMemo<Array<MRT_ColumnDef<AnlageStromstgListData>>>(
    () => [
      {
        header: "Bezeichnung",
        accessorKey: "name",
        Footer:
          list.length > 0 ? <span>Leistung stromsteuerliche Anlage</span> : null
      },
      {
        header: "Liegenschaft",
        accessorKey: "site"
      },
      {
        header: "Bruttoleistung",
        accessorKey: "bruttoleistung",
        Cell: ({ cell }) => (
          <BruttoleistungCell value={cell.getValue<number | null>()} />
        ),
        Footer:
          list.length > 0 ? (
            <span>
              <DecimalLabel value={totalBruttoLeistung} />
            </span>
          ) : null
      }
    ],
    [list, totalBruttoLeistung]
  );

  const match = useMatch(`${ROUTES.site.generator}/*`);
  const projectId = match?.params.projectId ?? null;
  const siteId = match?.params.siteId ?? null;
  const componentId = match?.params.componentId ?? null;

  const mrtTable = useMantineReactTable<AnlageStromstgListData>({
    ...getMinimalMRTOptions({
      emptyRowsFallbackText: "Keine Einheiten zugeordnet"
    }),
    enableSorting: true,
    columns,
    data: list
  });

  return (
    <div className="AnlageStromstgList">
      <Title mb="sm" order={6}>
        Zugeordnete Einheiten:
      </Title>
      {projectId && siteId && componentId && (
        <div className="controls">
          <Button
            component={Link}
            to={generatePath(`${ROUTES.site.generatorEditVerklammerung}/`, {
              projectId,
              siteId,
              componentId
            })}
          >
            Zuordnung der Einheit ändern
          </Button>
        </div>
      )}
      <MantineReactTable table={mrtTable} />
    </div>
  );
}

function BruttoleistungCell({ value }: { value: number | null }) {
  if (value === null) {
    return <span>Nicht eingetragen</span>;
  }

  return <DecimalLabel value={value} />;
}

function AnlageStromstgListDataWrapper({ id }: { id: number }) {
  const { anlagenStromstgGenerators, error, isLoading } =
    useAnlagenStromstgGenerators(id);

  return (
    <LoadOrError error={error} loading={isLoading}>
      {anlagenStromstgGenerators && (
        <AnlageStromstgList list={anlagenStromstgGenerators} />
      )}
    </LoadOrError>
  );
}

export {
  AnlageStromstgListDataWrapper as AnlageStromstgList,
  AnlageStromstgList as AnlageStromstgListNoLoader,
  AnlageStromstgListProps as AnlageStromstgListNoLoaderProps
};
